import React, { useState, useEffect } from "react";
import styles from "./Table.module.sass";
import cn from "classnames";
import Checkbox from "../../../components/Checkbox";
import Loader from "../../../components/Loader";
import Row from "./Row";
import { getContactRequests } from "../../../services/contactRequest.service";

const Table = ({ 
  className, 
  activeTable, 
  setActiveTable, 
  search, 
  onContactRequestSelect,
  selectedIds,
  setSelectedIds,
  contactRequests,
  setContactRequests
}) => {
  const [chooseAll, setСhooseAll] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [localContactRequests, setLocalContactRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);

  // Gestion de la sélection (comme PartnerList)
  useEffect(() => {
    if (localContactRequests.length > 0) {
      if (!activeId || !localContactRequests.find(item => item.id === activeId)) {
        setActiveId(localContactRequests[0]?.id || null);
      }
    } else {
      setActiveId(null);
    }
    setSelectedIds([]);
    setСhooseAll(false);
  }, [localContactRequests, activeId]);

  useEffect(() => {
    // Pas besoin de callback ici car on passe directement selectedIds
  }, [selectedIds]);

  const handleSelect = (id) => {
    setSelectedIds(prev => {
      const newSelected = prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id];
      setСhooseAll(newSelected.length === localContactRequests.length && localContactRequests.length > 0);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    const newChooseAll = !chooseAll;
    setСhooseAll(newChooseAll);
    setSelectedIds(newChooseAll ? localContactRequests.map(item => item.id) : []);
  };

  // Charger les demandes de contact
  const loadContactRequests = async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
        setNoResults(false);
      }

      const result = await getContactRequests({
        page,
        perPage: 10,
        search,
        sort: '-created'
      });

      if (isLoadMore) {
        setLocalContactRequests(prev => [...prev, ...result.items]);
        setContactRequests(prev => [...prev, ...result.items]);
      } else {
        setLocalContactRequests(result.items || []);
        setContactRequests(result.items || []);
        setNoResults(result.items.length === 0 && !!search);
        if (result.items.length > 0 && !activeId) {
          setActiveId(result.items[0].id);
        }
      }

      setCurrentPage(result.page);
      setTotalPages(result.totalPages);
    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error('Erreur lors du chargement des demandes de contact:', err);
        setError("Impossible de charger les demandes de contact. Veuillez réessayer.");
        setLocalContactRequests([]);
        setContactRequests([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Charger plus de données
  const handleLoadMore = () => {
    if (currentPage < totalPages && !loadingMore) {
      loadContactRequests(currentPage + 1, true);
    }
  };

  // Effet pour charger les données au montage et lors des changements de recherche
  useEffect(() => {
    loadContactRequests(1, false);
  }, [search]);

  // Effet pour transmettre la demande sélectionnée au composant parent
  useEffect(() => {
    if (activeId && localContactRequests.length > 0) {
      const selectedRequest = localContactRequests.find(request => request.id === activeId);
      if (selectedRequest && onContactRequestSelect) {
        onContactRequestSelect(selectedRequest);
      }
    }
  }, [activeId, localContactRequests, onContactRequestSelect]);

  return (
    <div className={cn(styles.wrapper, className)}>
      {error && <div className={styles.error}>{error}</div>}
      
      {loading ? (
        <div className={styles.loading}>Chargement des demandes de contact...</div>
      ) : (
        <>
          {noResults && localContactRequests.length === 0 && (
            <div className={styles.empty}>
              Aucune demande de contact trouvée pour votre recherche "{search}".
            </div>
          )}
          {!noResults && localContactRequests.length === 0 && !loading && !error && (
            <div className={styles.empty}>
              Aucune demande de contact disponible.
            </div>
          )}
          {localContactRequests.length > 0 && (
            <>
              <div className={cn(styles.table)}>
                <div className={cn(styles.row, { [styles.active]: activeTable })}>
                  <div className={styles.col}>
                    <Checkbox
                      className={styles.checkbox}
                      checked={chooseAll}
                      onChange={handleSelectAll}
                    />
                  </div>
                  <div className={styles.col}>Nom Prénom</div>
                  <div className={styles.col}>Email</div>
                  <div className={styles.col}>Type</div>
                  <div className={styles.col}>Date création</div>
                  <div className={styles.col}>Statut</div>
                </div>
                {localContactRequests.map((x, index) => (
                  <Row
                    item={x}
                    key={x.id}
                    activeTable={activeTable}
                    setActiveTable={setActiveTable}
                    activeId={activeId}
                    setActiveId={setActiveId}
                    selected={selectedIds.includes(x.id)}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
              {currentPage < totalPages && (
                <div className={styles.foot}>
                  <button 
                    className={cn("button-stroke button-small", styles.button)}
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore && <Loader className={styles.loader} />}
                    <span>{loadingMore ? 'Chargement...' : 'Charger plus'}</span>
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Table;
